import { CONVERSATION_MESSAGES_DEFAULT_SIZE } from '@consts';
import { SuccessResponseModel } from '@models';
import {
  CancelChatbotReplyResponse,
  ConversationModel,
  ConversationParticipantModel,
  ConversationSummariesResponseModel,
  CreatedMessageResponse,
  GetAttachmentsParamModel,
  MessageModel
} from '@models/conversations';
import { randomUUID } from '@random';
import * as Sentry from '@sentry/nextjs';
import { attachReplyToMessages } from '@services/conversations/conversationMessages';
import * as _ from 'lodash';
import { useCallback, useState } from 'react';
import { useImprvApi } from './useImprvApi';

/**
 * Custom hook for managing conversation-related operations
 */
export const useConversationsService = () => {
  const imprvApi = useImprvApi();

  // Loading states
  const [isPendingGetInboxSummaries, setIsPendingGetInboxSummaries] = useState(false);
  const [isPendingCleanInboxConversation, setIsPendingCleanInboxConversation] = useState(false);
  const [isPendingUncleanInboxConversation, setIsPendingUncleanInboxConversation] = useState(false);
  const [isPendingGetConversations, setIsPendingGetConversations] = useState(false);
  const [isPendingGetConversation, setIsPendingGetConversation] = useState(false);
  const [isPendingGetConversationMessages, setIsPendingGetConversationMessages] = useState(false);
  const [isPendingCreateConversationMessage, setIsPendingCreateConversationMessage] =
    useState(false);
  const [isPendingMarkMessageAsRead, setIsPendingMarkMessageAsRead] = useState(false);
  const [isPendingMarkMessageAsIrrelevant, setIsPendingMarkMessageAsIrrelevant] = useState(false);
  const [isPendingMarkMessageAsReplied, setIsPendingMarkMessageAsReplied] = useState(false);
  const [isPendingMarkMessageAsDeclinedForChatbot, setIsPendingMarkMessageAsDeclinedForChatbot] =
    useState(false);
  const [isPendingCancelChatbotReply, setIsPendingCancelChatbotReply] = useState(false);
  const [isPendingCreateParticipantPresence, setIsPendingCreateParticipantPresence] =
    useState(false);

  /**
   * Fetches inbox conversation summaries
   * @param params Optional pagination parameters and includeCleaned filter
   * @returns Promise resolving to ConversationSummariesResponseModel
   */
  const getInboxSummaries = useCallback(
    async (params?: {
      cursor?: string;
      size?: number;
      includeCleaned?: boolean;
      userIds?: number[];
      goalEntityIds?: string[];
    }): Promise<ConversationSummariesResponseModel> => {
      try {
        setIsPendingGetInboxSummaries(true);
        return await imprvApi.getInboxSummaries(params);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingGetInboxSummaries(false);
      }
    },
    [imprvApi]
  );

  /**
   * Cleans (marks as read/archived) a conversation from the inbox
   * @param conversationId The ID of the conversation to clean
   * @returns Promise resolving to SuccessResponseModel
   */
  const cleanInboxConversation = useCallback(
    async (conversationId: number): Promise<SuccessResponseModel> => {
      try {
        setIsPendingCleanInboxConversation(true);
        return await imprvApi.cleanInboxConversation(conversationId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingCleanInboxConversation(false);
      }
    },
    [imprvApi]
  );

  /**
   * Un-cleans a conversation (clears its done state in the inbox)
   * @param conversationId The ID of the conversation to un-clean
   * @returns Promise resolving to SuccessResponseModel
   */
  const uncleanInboxConversation = useCallback(
    async (conversationId: number): Promise<SuccessResponseModel> => {
      try {
        setIsPendingUncleanInboxConversation(true);
        return await imprvApi.uncleanInboxConversation(conversationId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingUncleanInboxConversation(false);
      }
    },
    [imprvApi]
  );

  /**
   * Fetches all conversations
   * @returns Promise resolving to array of ConversationModel
   */
  const getConversations = useCallback(async (): Promise<ConversationModel[]> => {
    try {
      setIsPendingGetConversations(true);
      const response = await imprvApi.getConversations();
      return response.conversations;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      setIsPendingGetConversations(false);
    }
  }, [imprvApi]);

  /**
   * Fetches a single conversation
   * @param id The conversation ID
   * @param options Optional options including AbortSignal for request cancellation
   * @returns Promise resolving to ConversationModel
   */
  const getConversation = useCallback(
    async (id: number, options?: { signal?: AbortSignal }): Promise<ConversationModel> => {
      try {
        setIsPendingGetConversation(true);
        return await imprvApi.getConversation(id, options);
      } catch (error) {
        // Don't log abort errors as they're expected when cancelling requests
        if (error instanceof Error && error.name !== 'AbortError') {
          Sentry.captureException(error);
        }
        throw error;
      } finally {
        setIsPendingGetConversation(false);
      }
    },
    [imprvApi]
  );

  /**
   * Fetches conversation messages
   * @param id The conversation ID
   * @param params Optional parameters (size, minCreatedAt)
   * @param params.size Maximum number of messages to fetch (default: 500)
   * @param params.minCreatedAt Only fetch messages created after this timestamp
   * @param options Optional options including AbortSignal for request cancellation
   * @returns Promise resolving to array of MessageModel
   * @remarks
   * **Important:** This function has a default limit of 500 messages per request.
   * For conversations with more than 500 messages, older messages will be truncated.
   * To fetch more messages, either:
   * - Specify a larger `size` parameter
   * - Use `minCreatedAt` to fetch newer messages incrementally
   * - Consider implementing pagination for full conversation history
   */
  const getConversationMessages = useCallback(
    async (
      id: number,
      params?: { size?: number; minCreatedAt?: string; maxCreatedAt?: string },
      options?: { signal?: AbortSignal }
    ): Promise<MessageModel[]> => {
      try {
        setIsPendingGetConversationMessages(true);
        const response = await imprvApi.getConversationMessages(
          id,
          {
            size: params?.size || CONVERSATION_MESSAGES_DEFAULT_SIZE,
            minCreatedAt: params?.minCreatedAt,
            maxCreatedAt: params?.maxCreatedAt
          },
          options
        );
        const messages = _.sortBy(response.messages, (m) => m.createdAt);
        return attachReplyToMessages(messages);
      } catch (error) {
        // Don't log abort errors as they're expected when cancelling requests
        if (error instanceof Error && error.name !== 'AbortError') {
          Sentry.captureException(error);
        }
        throw error;
      } finally {
        setIsPendingGetConversationMessages(false);
      }
    },
    [imprvApi]
  );

  /**
   * Fetches a single conversation message
   * @param conversationId The conversation ID
   * @param messageId The message ID
   * @returns Promise resolving to MessageModel
   */
  const getConversationMessage = useCallback(
    async (conversationId: number, messageId: number): Promise<MessageModel> => {
      try {
        return await imprvApi.getConversationMessage(conversationId, messageId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  /**
   * Creates a new conversation message.
   * @param id The conversation ID.
   * @param payload Message payload. `context` is optional and, when present,
   *   is forwarded to the backend so the chatbot can condition its reply on
   *   the user's current task / block (see MessageContext).
   * @returns Promise resolving to CreatedMessageResponse with success, id, and created_at.
   */
  const createConversationMessage = useCallback(
    async (
      id: number,
      payload: {
        content: string;
        inReplyToMessageId?: number;
        messageRecommendationId?: number;
        cid?: string;
        context?: {
          taskEntityId?: string | null;
          blockEntityId?: string | null;
        };
      },
      options?: {
        keepalive?: boolean;
      }
    ): Promise<CreatedMessageResponse> => {
      try {
        setIsPendingCreateConversationMessage(true);
        const response = await imprvApi.createConversationMessage(
          id,
          {
            ...payload,
            cid: payload.cid || randomUUID()
          },
          options
        );
        return response;
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingCreateConversationMessage(false);
      }
    },
    [imprvApi]
  );

  /**
   * Marks a conversation message as read
   * @param conversationId The conversation ID
   * @param messageId The message ID
   * @returns Promise resolving to SuccessResponseModel
   */
  const markConversationMessageAsRead = useCallback(
    async (conversationId: number, messageId: number): Promise<SuccessResponseModel> => {
      try {
        setIsPendingMarkMessageAsRead(true);
        return await imprvApi.markConversationMessageAsRead(conversationId, messageId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingMarkMessageAsRead(false);
      }
    },
    [imprvApi]
  );

  /**
   * Marks a conversation message as irrelevant
   * @param conversationId The conversation ID
   * @param messageId The message ID
   * @returns Promise resolving to SuccessResponseModel
   */
  const markConversationMessageAsIrrelevant = useCallback(
    async (conversationId: number, messageId: number): Promise<SuccessResponseModel> => {
      try {
        setIsPendingMarkMessageAsIrrelevant(true);
        return await imprvApi.markConversationMessageAsIrrelevant(conversationId, messageId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingMarkMessageAsIrrelevant(false);
      }
    },
    [imprvApi]
  );

  /**
   * Marks a conversation message as replied
   * @param conversationId The conversation ID
   * @param messageId The message ID
   * @returns Promise resolving to SuccessResponseModel
   */
  const markConversationMessageAsReplied = useCallback(
    async (conversationId: number, messageId: number): Promise<SuccessResponseModel> => {
      try {
        setIsPendingMarkMessageAsReplied(true);
        return await imprvApi.markConversationMessageAsReplied(conversationId, messageId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingMarkMessageAsReplied(false);
      }
    },
    [imprvApi]
  );

  /**
   * Marks a conversation message as declined for chatbot
   * @param conversationId The conversation ID
   * @param messageId The message ID
   * @returns Promise resolving to SuccessResponseModel
   */
  const markConversationMessageAsDeclinedForChatbot = useCallback(
    async (conversationId: number, messageId: number): Promise<SuccessResponseModel> => {
      try {
        setIsPendingMarkMessageAsDeclinedForChatbot(true);
        return await imprvApi.markConversationMessageAsDeclinedForChatbot(
          conversationId,
          messageId
        );
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingMarkMessageAsDeclinedForChatbot(false);
      }
    },
    [imprvApi]
  );

  const cancelChatbotReply = useCallback(
    async (conversationId: number, messageId: number): Promise<CancelChatbotReplyResponse> => {
      try {
        setIsPendingCancelChatbotReply(true);
        return await imprvApi.cancelChatbotReply(conversationId, messageId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingCancelChatbotReply(false);
      }
    },
    [imprvApi]
  );

  /**
   * Gets conversation attachments
   * @param id The conversation ID
   * @param params Attachment query parameters
   * @returns Promise resolving to array of attachments
   */
  const getConversationAttachments = useCallback(
    async (id: number, params: GetAttachmentsParamModel): Promise<any[]> => {
      try {
        const response = await imprvApi.getConversationAttachments(id, params);
        return response.attachments;
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  /**
   * Creates conversation participant presence
   * @param conversationId The conversation ID
   * @returns Promise resolving to SuccessResponseModel
   */
  const createConversationParticipantPresence = useCallback(
    async (conversationId: number): Promise<SuccessResponseModel> => {
      try {
        setIsPendingCreateParticipantPresence(true);
        return await imprvApi.createConversationParticipantPresence(conversationId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingCreateParticipantPresence(false);
      }
    },
    [imprvApi]
  );

  /**
   * Marks a message recommendation as declined
   * @param messageRecommendationId The message recommendation ID
   * @returns Promise resolving to SuccessResponseModel
   */
  const markMessageRecommendationAsDeclined = useCallback(
    async (messageRecommendationId: number): Promise<SuccessResponseModel> => {
      try {
        return await imprvApi.markMessageRecommendationAsDeclined(messageRecommendationId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  /**
   * Gets the me participant and if the conversation is viewed by the producer
   * @param participants The conversation participants
   * @returns The me participant and if the conversation is viewed by the producer
   */
  const getConversationParticipantContext = useCallback(
    (
      participants: ConversationParticipantModel[]
    ): { me: ConversationParticipantModel | undefined; isViewedByProducer: boolean } => {
      const me = participants.find((participant) => participant.user.isMe);
      return { me, isViewedByProducer: !!me?.isProducer };
    },
    []
  );

  return {
    // Inbox
    getInboxSummaries,
    isPendingGetInboxSummaries,
    cleanInboxConversation,
    isPendingCleanInboxConversation,
    uncleanInboxConversation,
    isPendingUncleanInboxConversation,

    // Conversations
    getConversations,
    isPendingGetConversations,
    getConversation,
    isPendingGetConversation,
    getConversationParticipantContext,

    // Messages
    getConversationMessages,
    isPendingGetConversationMessages,
    getConversationMessage,
    createConversationMessage,
    isPendingCreateConversationMessage,

    // Message actions
    markConversationMessageAsRead,
    isPendingMarkMessageAsRead,
    markConversationMessageAsIrrelevant,
    isPendingMarkMessageAsIrrelevant,
    markConversationMessageAsReplied,
    isPendingMarkMessageAsReplied,
    markConversationMessageAsDeclinedForChatbot,
    isPendingMarkMessageAsDeclinedForChatbot,
    cancelChatbotReply,
    isPendingCancelChatbotReply,

    // Attachments
    getConversationAttachments,

    // Presence
    createConversationParticipantPresence,
    isPendingCreateParticipantPresence,

    // Recommendations
    markMessageRecommendationAsDeclined
  };
};
export default useConversationsService;
